/**
 * Hybrid snap router — ports the validated experiments (experiments/route-spike,
 * experiments/offtrail) into the app:
 *   - on-graph: turf noding of visible trail lines + A*  (snap to trails)
 *   - off-graph: corridor-bounded DEM least-cost (Tobler) over z16 Mapterhorn,
 *     tiles pulled LAZILY along the explored corridor, moving-average smoothed
 *                                                        (snap to terrain)
 * Routes one segment at a time; DEM tiles are fetched on demand as the search
 * advances, so cost stays bounded by the corridor — never a full bbox rectangle.
 *
 * This deliberately does NOT use route-planner-service (coarse z12 grid). The DEM
 * here is z16 (~0.9 m) — the resolution the off-trail/couloir work needs.
 */
import {
  lineIntersect,
  lineSlice,
  nearestPointOnLine,
  length as turfLength,
  distance as turfDistance,
  point,
  lineString,
} from '@turf/turf'
import type { Feature, LineString } from 'geojson'
import { demRouteWorker } from './dem-route-client.js'
import { logger } from '../../utils/logger.js'

const log = logger.create('hybrid-router')

export type LL = [number, number] // [lng, lat]
export interface SnapOpts {
  snapTrails: boolean
  snapTerrain: boolean
  lineFeatures?: Feature<LineString>[]
  /** "on trail" distance in meters — caller passes a zoom-derived (≈few px) value
   *  so it means genuinely on the line, not tens of meters off (avoids the jaunt). */
  snapTolM?: number
  /** Prebuilt (cached) trail graph to route against — avoids re-noding every call
   *  (e.g. the hover preview already built it). Snaps are undone after each segment. */
  graph?: Graph
}

// ───────────────────────── min-heap ─────────────────────────
class Heap {
  private a: [number, number][] = []
  get size(): number {
    return this.a.length
  }
  push(x: [number, number]): void {
    const a = this.a
    a.push(x)
    let i = a.length - 1
    while (i > 0) {
      const p = (i - 1) >> 1
      if (a[p][0] <= a[i][0]) break
      ;[a[p], a[i]] = [a[i], a[p]]
      i = p
    }
  }
  pop(): [number, number] {
    const a = this.a,
      top = a[0],
      last = a.pop()!
    if (a.length) {
      a[0] = last
      let i = 0
      for (;;) {
        const l = 2 * i + 1,
          r = l + 1
        let m = i
        if (l < a.length && a[l][0] < a[m][0]) m = l
        if (r < a.length && a[r][0] < a[m][0]) m = r
        if (m === i) break
        ;[a[m], a[i]] = [a[i], a[m]]
        i = m
      }
    }
    return top
  }
}

// DEM corridor tuning — passed through routePath to the DEM worker.
export interface DemTuning {
  bufferM?: number // corridor half-width around the straight line (m)
  directness?: number // hours-per-km penalty on distance; bigger = straighter
}
// ───────────────────────── trail graph (on-graph) ─────────────────────────
export interface Graph {
  adj: Map<string, { v: string; geom: LL[]; cost: number }[]>
  nodeC: Map<string, LL>
  edges: { geom: LL[] }[]
}
const KEYP = 5,
  nkey = (c: LL): string => `${c[1].toFixed(KEYP)},${c[0].toFixed(KEYP)}`
const meters = (coords: LL[]): number =>
  turfLength(lineString(coords), { units: 'kilometers' }) * 1000

// Drop near-coincident duplicate lines (same real trail drawn by multiple layers/sources).
// Without this, noding the duplicates makes a ladder of spurious junctions → routing jaunts.
function dedupeCoincident(features: Feature<LineString>[], bufM = 8): Feature<LineString>[] {
  const valid = features.filter(
    f => f.geometry?.type === 'LineString' && f.geometry.coordinates.length >= 2
  )
  // keep the longest representative of each coincident cluster
  const sorted = [...valid].sort(
    (a, b) => meters(b.geometry.coordinates as LL[]) - meters(a.geometry.coordinates as LL[])
  )
  const kept: Feature<LineString>[] = []
  for (const f of sorted) {
    const co = f.geometry.coordinates as LL[]
    const n = Math.min(10, co.length)
    const samples: LL[] = []
    for (let s = 0; s < n; s++) samples.push(co[Math.round((s * (co.length - 1)) / (n - 1))])
    const dup = kept.some(k => {
      const kl = lineString(k.geometry.coordinates)
      let inb = 0
      for (const p of samples)
        if (nearestPointOnLine(kl, point(p)).properties.dist * 1000 <= bufM) inb++
      return inb / samples.length >= 0.8 // ≥80% of this line lies on top of a kept line
    })
    if (!dup) kept.push(f)
  }
  return kept
}

export function buildTrailGraph(features: Feature<LineString>[]): Graph {
  const adj = new Map<string, { v: string; geom: LL[]; cost: number }[]>(),
    nodeC = new Map<string, LL>(),
    edges: { geom: LL[] }[] = []
  const lines = dedupeCoincident(features)
  const split: LL[][] = lines.map(() => [])
  // bbox prefilter: only run line-intersect on pairs whose extents overlap (keeps
  // "all rendered lines" cheap — most pairs are nowhere near each other).
  const bbox = (co: LL[]): [number, number, number, number] => {
    let w = Infinity,
      s = Infinity,
      e = -Infinity,
      n = -Infinity
    for (const c of co) {
      if (c[0] < w) w = c[0]
      if (c[0] > e) e = c[0]
      if (c[1] < s) s = c[1]
      if (c[1] > n) n = c[1]
    }
    return [w, s, e, n]
  }
  const bx = lines.map(l => bbox(l.geometry.coordinates as LL[]))
  const overlap = (a: number[], b: number[]): boolean =>
    a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]
  for (let i = 0; i < lines.length; i++)
    for (let j = i + 1; j < lines.length; j++) {
      if (!overlap(bx[i], bx[j])) continue
      for (const p of lineIntersect(lines[i], lines[j]).features) {
        const c = p.geometry.coordinates as LL
        split[i].push(c)
        split[j].push(c)
      }
    }
  const addAdj = (u: string, v: string, geom: LL[], cost: number): void => {
    if (!adj.has(u)) adj.set(u, [])
    adj.get(u)!.push({ v, geom, cost })
  }
  for (let i = 0; i < lines.length; i++) {
    const co = lines[i].geometry.coordinates as LL[]
    const bps = [co[0], co[co.length - 1], ...split[i]]
      .map(p => ({ p, loc: nearestPointOnLine(lines[i], point(p)).properties.location }))
      .sort((a, b) => a.loc - b.loc)
    for (let k = 0; k < bps.length - 1; k++) {
      if (nkey(bps[k].p) === nkey(bps[k + 1].p)) continue
      const seg = lineSlice(point(bps[k].p), point(bps[k + 1].p), lines[i]).geometry
        .coordinates as LL[]
      if (seg.length < 2) continue
      const a = nkey(seg[0]),
        b = nkey(seg[seg.length - 1]),
        cost = meters(seg)
      addAdj(a, b, seg, cost)
      addAdj(b, a, [...seg].reverse(), cost)
      edges.push({ geom: seg })
      nodeC.set(a, seg[0])
      nodeC.set(b, seg[seg.length - 1])
    }
  }
  return { adj, nodeC, edges }
}

interface Snap {
  nk: string
  c: LL
  dist: number
  edgeIdx: number
  edgeGeom: LL[]
  cleanup: () => void
}
function snapToGraph(graph: Graph, ll: LL): Snap {
  let best: { geom: LL[]; c: LL } | null = null,
    bd = Infinity,
    bestIdx = -1
  for (let i = 0; i < graph.edges.length; i++) {
    const np = nearestPointOnLine(lineString(graph.edges[i].geom), point(ll))
    if (np.properties.dist < bd) {
      bd = np.properties.dist
      best = { geom: graph.edges[i].geom, c: np.geometry.coordinates as LL }
      bestIdx = i
    }
  }
  if (!best)
    return { nk: nkey(ll), c: ll, dist: Infinity, edgeIdx: -1, edgeGeom: [], cleanup: () => {} }
  const nk = nkey(best.c)
  const s1 = lineSlice(point(best.geom[0]), point(best.c), lineString(best.geom)).geometry
    .coordinates as LL[]
  const s2 = lineSlice(point(best.c), point(best.geom[best.geom.length - 1]), lineString(best.geom))
    .geometry.coordinates as LL[]
  const a0 = nkey(best.geom[0]),
    b0 = nkey(best.geom[best.geom.length - 1])
  const hadNkC = graph.nodeC.has(nk)
  // Track the exact entries (and any keys) we add, so cleanup removes precisely those
  // by reference. Length-based restore is order-dependent: when a route's two ends
  // snap either side of a SHARED junction node, undoing them out of order grows an
  // adjacency array (arr.length = prev) and leaves an `undefined` hole that corrupts
  // the cached graph. Reference removal is order-independent and can't corrupt it.
  type AdjEdge = { v: string; geom: LL[]; cost: number }
  const added: Array<{ k: string; e: AdjEdge }> = []
  const createdKeys: string[] = []
  const add = (u: string, v: string, geom: LL[]): void => {
    if (geom.length < 2) return
    let arr = graph.adj.get(u)
    if (!arr) {
      arr = []
      graph.adj.set(u, arr)
      createdKeys.push(u)
    }
    const e: AdjEdge = { v, geom, cost: meters(geom) }
    arr.push(e)
    added.push({ k: u, e })
  }
  // s1 runs a0→nk, s2 runs nk→b0 — orient each stored edge as u→v
  add(a0, nk, s1)
  add(nk, a0, [...s1].reverse())
  add(b0, nk, [...s2].reverse())
  add(nk, b0, s2)
  graph.nodeC.set(nk, best.c)
  const cleanup = (): void => {
    for (const { k, e } of added) {
      const arr = graph.adj.get(k)
      if (!arr) continue
      const i = arr.indexOf(e)
      if (i >= 0) arr.splice(i, 1)
    }
    for (const k of createdKeys) {
      const arr = graph.adj.get(k)
      if (arr?.length === 0) graph.adj.delete(k)
    }
    if (!hadNkC) graph.nodeC.delete(nk)
  }
  return { nk, c: best.c, dist: bd * 1000, edgeIdx: bestIdx, edgeGeom: best.geom, cleanup }
}

function graphRoute(graph: Graph, start: string, goal: string): LL[] | null {
  const gc = graph.nodeC.get(goal)
  if (!gc) return null
  const h = (n: string): number => {
    const c = graph.nodeC.get(n)!
    return turfDistance(point(c), point(gc), { units: 'meters' })
  }
  const g = new Map([[start, 0]]),
    came = new Map<string, { u: string; geom: LL[] }>(),
    done = new Set<string>()
  const open = new Heap()
  const ids: string[] = [start]
  open.push([h(start), 0])
  while (open.size) {
    const u = ids[open.pop()[1]]
    if (u === goal) break
    if (done.has(u)) continue
    done.add(u)
    for (const e of graph.adj.get(u) || []) {
      const ng = (g.get(u) ?? Infinity) + e.cost
      if (ng < (g.get(e.v) ?? Infinity)) {
        g.set(e.v, ng)
        came.set(e.v, { u, geom: e.geom })
        ids.push(e.v)
        open.push([ng + h(e.v), ids.length - 1])
      }
    }
  }
  if (!came.has(goal)) return null
  const out: LL[] = []
  let cur = goal
  while (cur !== start) {
    const { u, geom } = came.get(cur)!
    // geom must run u→cur for the stitched path to be continuous; reverse if stored flipped
    out.unshift(...(nkey(geom[geom.length - 1]) === cur ? geom : [...geom].reverse()))
    cur = u
  }
  return out
}

// ───────────────────────── hybrid dispatch ─────────────────────────
const TOL_M = 10 // fallback "on trail" distance (m); caller normally passes a zoom-derived snapTolM

/** Route the polyline through `waypoints`, filling each segment per the snap mode. */
export async function routePath(waypoints: LL[], opts: SnapOpts): Promise<LL[]> {
  if (waypoints.length < 2) return waypoints
  // Reuse a cached graph when given (hover preview already built it); else node now.
  const graph =
    opts.graph ??
    (opts.snapTrails && opts.lineFeatures?.length ? buildTrailGraph(opts.lineFeatures) : null)
  const tol = opts.snapTolM ?? TOL_M
  if (graph)
    log.info(
      `[snap] graph: ${graph.edges.length} edges, ${graph.nodeC.size} nodes; on-trail tol=${tol.toFixed(0)}m`
    )
  const out: LL[] = [waypoints[0]]
  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1],
      b = waypoints[i]
    const sa = graph ? snapToGraph(graph, a) : null
    const sb = graph ? snapToGraph(graph, b) : null
    const aOn = !!sa && sa.dist <= tol
    const bOn = !!sb && sb.dist <= tol
    // widen the DEM corridor for longer legs so the path can bow around terrain, but
    // cap at ~1 tile half-width (≈2 tiles wide) — beyond that you're way off the line
    const legM = turfDistance(point(a), point(b), { units: 'meters' })
    const demTune: DemTuning = { bufferM: Math.max(120, Math.min(460, legM * 0.35)) }
    let seg: LL[] | null = null
    let mode = 'straight'

    if (graph && sa && sb && aOn && bOn) {
      // both ends on a trail → follow the trail. Never DEM.
      if (sa.edgeIdx >= 0 && sa.edgeIdx === sb.edgeIdx) {
        // same trail edge → slice directly between the two points (no out-and-back to a node)
        const slice = lineSlice(point(sa.c), point(sb.c), lineString(sa.edgeGeom)).geometry
          .coordinates as LL[]
        // orient the slice a→b so the stitched segment doesn't dart to the far end and back
        const d0 = turfDistance(point(slice[0]), point(sa.c)),
          dN = turfDistance(point(slice[slice.length - 1]), point(sa.c))
        seg = [a, ...(d0 <= dN ? slice : [...slice].reverse()), b]
        mode = 'trail(same-edge)'
      } else {
        const gp = graphRoute(graph, sa.nk, sb.nk)
        if (gp) {
          seg = [a, ...gp, b]
          mode = 'trail'
        }
      }
    }
    // Anything not fully on-trail (one end off, or both off) is a terrain segment
    // straight between the two clicked points — the on-trail end IS the join point,
    // so we never detour to a nearby existing track vertex.
    if (!seg && opts.snapTerrain) {
      const d = await demRouteWorker(a, b, demTune)
      if (d) {
        seg = d
        mode = 'dem'
      }
    }
    if (!seg) {
      seg = [a, b]
      mode = 'straight'
    }
    // Undo the snap mutations so a cached graph stays pristine for the next call.
    sa?.cleanup()
    sb?.cleanup()
    log.info(`[snap] seg${i}: aOn=${aOn} bOn=${bOn} -> ${mode} (${seg.length} pts)`)
    out.push(...seg.slice(1))
  }
  return out
}

/**
 * Cheap hover preview while drawing a line: snaps the cursor to the nearest visible
 * track and, if both the last anchor and the cursor lie on the same track, previews
 * the slice along it. Non-mutating and noding-free so it's safe on every mousemove.
 * Terrain (DEM) routing is intentionally NOT previewed — too costly per move.
 */
export function previewToCursor(
  features: Feature<LineString>[],
  last: LL,
  cursor: LL,
  snapTolM: number,
  snapTrails: boolean
): { coords: LL[]; cursorOnTrail: boolean; snapPoint: LL | null } {
  if (!snapTrails) return { coords: [last, cursor], cursorOnTrail: false, snapPoint: null }
  let best: { c: LL; geom: LL[]; dist: number } | null = null
  for (const f of features) {
    if (f.geometry?.type !== 'LineString' || f.geometry.coordinates.length < 2) continue
    const np = nearestPointOnLine(f, point(cursor))
    const d = (np.properties.dist ?? Infinity) * 1000
    if (!best || d < best.dist)
      best = { c: np.geometry.coordinates as LL, geom: f.geometry.coordinates as LL[], dist: d }
  }
  if (!best || best.dist > snapTolM)
    return { coords: [last, cursor], cursorOnTrail: false, snapPoint: null }
  // Cursor snaps to a track. If the last anchor sits on that same track, trace the
  // slice between them; otherwise just a straight segment to the snapped point.
  const lastNp = nearestPointOnLine(lineString(best.geom), point(last))
  if ((lastNp.properties.dist ?? Infinity) * 1000 <= snapTolM) {
    const lc = lastNp.geometry.coordinates as LL
    const slice = lineSlice(point(lc), point(best.c), lineString(best.geom)).geometry
      .coordinates as LL[]
    const d0 = turfDistance(point(slice[0]), point(lc))
    const dN = turfDistance(point(slice[slice.length - 1]), point(lc))
    const oriented = d0 <= dN ? slice : [...slice].reverse()
    return { coords: [last, ...oriented, best.c], cursorOnTrail: true, snapPoint: best.c }
  }
  return { coords: [last, best.c], cursorOnTrail: true, snapPoint: best.c }
}

/**
 * Hover preview using a prebuilt (cached) trail graph: follows the connected track
 * across noded edges all the way to the cursor — matching what a click would route.
 * Snaps are undone after routing so the cached graph isn't mutated between calls.
 * Off-trail anchors aren't DEM-previewed (too costly per move) → straight segment.
 */
export function previewRoute(
  graph: Graph,
  last: LL,
  cursor: LL,
  snapTolM: number
): { coords: LL[]; cursorOnTrail: boolean; snapPoint: LL | null } {
  const sb = snapToGraph(graph, cursor)
  if (sb.dist > snapTolM) {
    sb.cleanup()
    return { coords: [last, cursor], cursorOnTrail: false, snapPoint: null }
  }
  const sa = snapToGraph(graph, last)
  let coords: LL[]
  if (sa.dist <= snapTolM) {
    if (sa.edgeIdx >= 0 && sa.edgeIdx === sb.edgeIdx) {
      const slice = lineSlice(point(sa.c), point(sb.c), lineString(sa.edgeGeom)).geometry
        .coordinates as LL[]
      const d0 = turfDistance(point(slice[0]), point(sa.c))
      const dN = turfDistance(point(slice[slice.length - 1]), point(sa.c))
      coords = [last, ...(d0 <= dN ? slice : [...slice].reverse()), sb.c]
    } else {
      const gp = graphRoute(graph, sa.nk, sb.nk)
      coords = gp ? [last, ...gp, sb.c] : [last, sb.c]
    }
  } else {
    coords = [last, sb.c] // last off-trail → straight to the snapped point
  }
  sa.cleanup()
  sb.cleanup()
  return { coords, cursorOnTrail: true, snapPoint: sb.c }
}

/** Is a clicked point on a routable line (for the "start on a line → snap" default)? */
export function isOnTrail(ll: LL, features: Feature<LineString>[], tolM = TOL_M): boolean {
  for (const f of features) {
    if (f.geometry?.type !== 'LineString') continue
    const np = nearestPointOnLine(f, point(ll))
    if (np.properties.dist * 1000 <= tolM) return true
  }
  return false
}
